// policy-builder.js — visual rule builder for the Policy page.
//
// Authors a Cedar-shaped rule by completing a sentence with chips
// + popover pickers ("Block | Sandbox agents | Bash/Edit | on
// /payments/**"). Live preview at the bottom shows the rule as
// English + a 24-hour simulator stat.
//
// State shape: every field the user can touch lives on
// builderState as a structured value. The legacy hidden inputs
// in the modal (builder-decision, builder-actions, etc) are
// mirrored from this state at save time so downstream
// collectFormToRule + handleBuilderSave keep working without
// a full rewrite of the save plumbing.
//
// builderState is reset every open via openPolicyBuilder, so
// leftover state from a previous session can't leak.
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 1a).
import { policyState } from "./page-state.js";
import { apiFetch } from "./api.js";
import { escapeHTML, humanList } from "./util.js";
import { showToast, showConfirm } from "./ui.js";
import { renderPolicyPage } from "./pages/policy.js";
import { ruleToWriteShape } from "./rule-shape.js";

const DEFAULT_ACTIONS = ["Bash", "Edit", "Write", "Read"];

const DECISION_OPTIONS = [
  {
    value: "forbid",
    label: "Block",
    sub: "Hard-deny the action.",
    chipClass: "chip-danger",
  },
  {
    value: "require_confirm",
    label: "Ask first",
    sub: "Require human confirmation before proceeding.",
    chipClass: "chip-warning",
  },
  {
    value: "audit",
    label: "Audit only",
    sub: 'Log a "would have blocked" event, but allow it.',
    chipClass: "chip-accent",
  },
];

// v0.39.0 role vocabulary, ordered highest authority → lowest so the
// dropdown reads like the rank hierarchy. The daemon resolves these (and
// the legacy Driver/Responder/Routine/Worker/Sandbox names, via aliases).
const WHO_ROLE_OPTIONS = ["human", "reviewer", "background", "batch", "sandbox"];

const builderState = {
  decision: "forbid", // 'forbid' | 'require_confirm' | 'audit'
  //   (decision: 'audit' is sugar for
  //   forbid+mode:audit; collapsed into one
  //   user-facing choice)
  who: { kind: "any" }, // { kind: 'any' } | { kind: 'role', role: 'Sandbox' }
  //   | { kind: 'agent_id', id: '<spiffe>' }
  actions: [], // ['Bash', 'Edit', ...]
  paths: [], // [{ path: '/foo/**' }, ...] — list to allow
  //   multiple globs in advanced; sentence
  //   shows the first one + chip for each extra
  branches: [], // ['main', 'master', ...] — multi-select
  //   chip group. Empty = rule fires on any
  //   branch. First entry mirrors to legacy
  //   constraint.resource.branch.
  reason: "",
  rule: null, // last-built Rule object
  previewResp: null, // last /v1/rules/draft/preview response
  editingName: null, // PUT when set
  editingOriginalRule: null, // for mergePreservedFields
};

// resetBuilder clears the sentence state and the Save button before
// every open, so nothing from the last rule (or a finished save) leaks in.
function resetBuilder() {
  builderState.decision = "forbid";
  builderState.who = { kind: "any" };
  builderState.actions = [];
  builderState.paths = [];
  builderState.branches = [];
  builderState.reason = "";
  builderState.rule = null;
  builderState.previewResp = null;
  builderState.editingName = null;
  builderState.editingOriginalRule = null;
  const save = document.getElementById("policy-builder-save");
  if (save) {
    save.disabled = false;
    save.textContent = "Save rule";
  }
  clearBuilderForm();
}

export function openPolicyBuilder() {
  const modal = document.getElementById("policy-builder-modal");
  if (!modal) return;
  resetBuilder();
  document.getElementById("policy-builder-error").textContent = "";
  setBuilderTitle("New rule", "Author a rule by filling in the sentence below.");
  modal.style.display = "flex";
  renderBuilderState();
  schedulePreviewUpdate();
}

export function openPolicyBuilderForEdit(rule) {
  const modal = document.getElementById("policy-builder-modal");
  if (!modal) return;
  resetBuilder();
  builderState.editingName = rule.name;
  builderState.editingOriginalRule = rule;
  seedFormFromRule(rule);
  document.getElementById("policy-builder-error").textContent = "";
  setBuilderTitle("Edit rule", 'Editing "' + rule.name + '".');
  modal.style.display = "flex";
  renderBuilderState();
  schedulePreviewUpdate();
}

// openPolicyBuilderForSuggestedRule opens the builder pre-filled from a draft
// Rule — used by the Protection-page recommendations to turn a flagged pattern
// (e.g. repeated branch.contended on "main") into a starting rule the user can
// review, preview, and save. It reuses seedFormFromRule to project the draft
// into the sentence-state, but leaves editingName=null so Save creates a NEW
// rule (POST /v1/rules/draft/append) rather than editing an existing one. The
// name stays editable (it's a fresh rule). Same pre-fill mechanism as the
// Discovered-Tools "+ Rule" deep-link (openPolicyBuilderForTool).
export function openPolicyBuilderForSuggestedRule(draftRule, subtitle) {
  const modal = document.getElementById("policy-builder-modal");
  if (!modal) return;
  resetBuilder(); // a NEW rule, not an edit
  seedFormFromRule(draftRule);
  // seedFormFromRule marks the name read-only (it assumes an existing rule);
  // a suggested rule is brand new, so let the user name/rename it.
  const nameInput = document.getElementById("builder-name");
  if (nameInput) {
    nameInput.readOnly = false;
    if (draftRule.name) nameInput.value = draftRule.name;
  }
  document.getElementById("policy-builder-error").textContent = "";
  setBuilderTitle(
    "Suggested rule",
    subtitle || "Review and save — this rule will govern future agent actions.",
  );
  modal.style.display = "flex";
  renderBuilderState();
  schedulePreviewUpdate();
}

function setBuilderTitle(title, subtitle) {
  const el = document.getElementById("policy-builder-title");
  if (el) el.textContent = title;
  const subEl = el && el.nextElementSibling;
  if (subEl && typeof subtitle === "string") subEl.textContent = subtitle;
}

function clearBuilderForm() {
  // Sentence state already reset by caller. Mirror legacy hidden
  // inputs so save plumbing doesn't see stale values.
  document.getElementById("builder-name").value = "";
  document.getElementById("builder-decision").value = "forbid";
  const modeSel = document.getElementById("builder-mode");
  if (modeSel) modeSel.value = "enforce";
  document.getElementById("builder-reason").value = "";
  document.getElementById("builder-resource-path").value = "";
  document.getElementById("builder-resource-branch").value = "";
  document.getElementById("builder-actions").value = "";
  document.getElementById("builder-principal-role").value = "";
  document.getElementById("builder-principal-seniority").value = "";
  document.getElementById("builder-principal-domain").value = "";
  document.querySelectorAll("[data-seniority-check]").forEach((cb) => {
    cb.checked = false;
  });
  const agentIDInput = document.getElementById("builder-principal-agent-id");
  if (agentIDInput) agentIDInput.value = "";
  // Grace defaults to 600; keep input value rather than blanking
  // so user sees the default the moment they switch to "Ask first".
  const graceInp = document.getElementById("builder-grace");
  if (graceInp) graceInp.value = "600";
  document.getElementById("builder-name").readOnly = false;
}

// seedFormFromRule projects a Rule from /v1/rules into the
// sentence-state. Same role as the legacy version but it writes
// to builderState (structured) instead of bare inputs.
function seedFormFromRule(rule) {
  document.getElementById("builder-name").value = rule.name || "";
  document.getElementById("builder-name").readOnly = true;
  builderState.reason = rule.reason || "";
  document.getElementById("builder-reason").value = builderState.reason;
  const decision =
    rule.decision === "require_confirm" ? "require_confirm" : "forbid";
  // The unified user-facing decision collapses (decision, mode) into
  // three radio choices: forbid → "Block", require_confirm → "Ask
  // first", anything with mode==audit → "Audit only" (always renders
  // as forbid under the hood since audit + require_confirm is
  // semantically weird).
  if (rule.mode === "audit") builderState.decision = "audit";
  else builderState.decision = decision;
  document.getElementById("builder-decision").value = decision;
  const modeSel = document.getElementById("builder-mode");
  if (modeSel) modeSel.value = rule.mode === "audit" ? "audit" : "enforce";
  if (decision === "require_confirm" && rule.grace_seconds) {
    document.getElementById("builder-grace").value = rule.grace_seconds;
  }
  const c = rule.constraint || {};
  if (c.resource) {
    const p = c.resource.path || "";
    if (p) builderState.paths = [{ path: p }];
    document.getElementById("builder-resource-path").value = p;
    if (c.resource.branch) builderState.branches = [c.resource.branch];
    const branchInp = document.getElementById("builder-resource-branch");
    if (branchInp) branchInp.value = builderState.branches[0] || "";
  }
  if (Array.isArray(c.action)) {
    builderState.actions = c.action.slice();
    document.getElementById("builder-actions").value = c.action.join(", ");
  }
  const who = c.principal;
  if (who) {
    if (Array.isArray(who.agent_id) && who.agent_id.length === 1) {
      builderState.who = { kind: "agent_id", id: who.agent_id[0] };
    } else if (Array.isArray(who.role) && who.role.length === 1) {
      builderState.who = { kind: "role", role: who.role[0] };
    }
    if (Array.isArray(who.role)) {
      document.getElementById("builder-principal-role").value =
        who.role.join(", ");
    }
    if (Array.isArray(who.seniority)) {
      document.getElementById("builder-principal-seniority").value =
        who.seniority.join(", ");
      const wanted = new Set(who.seniority);
      document.querySelectorAll("[data-seniority-check]").forEach((cb) => {
        cb.checked = wanted.has(cb.dataset.seniorityCheck);
      });
    }
    if (Array.isArray(who.domain)) {
      document.getElementById("builder-principal-domain").value =
        who.domain.join(", ");
    }
    if (Array.isArray(who.agent_id)) {
      const inp = document.getElementById("builder-principal-agent-id");
      if (inp) inp.value = who.agent_id.join(", ");
    }
  }
}

export function closePolicyBuilder() {
  const modal = document.getElementById("policy-builder-modal");
  if (modal) modal.style.display = "none";
  hideBuilderPopover();
}

function renderBuilderState() {
  const dec =
    DECISION_OPTIONS.find((o) => o.value === builderState.decision) ||
    DECISION_OPTIONS[0];
  const decLabel = document.getElementById("builder-decision-label");
  if (decLabel) decLabel.textContent = dec.label;
  const whoLabel = document.getElementById("builder-who-label");
  if (whoLabel) {
    if (builderState.who.kind === "role")
      whoLabel.textContent = builderState.who.role + " agents";
    else if (builderState.who.kind === "agent_id")
      whoLabel.textContent = "session " + truncMid(builderState.who.id, 24);
    else whoLabel.textContent = "any agent";
  }
  const actionsHost = document.getElementById("builder-actions-host");
  if (actionsHost) {
    const chips = builderState.actions
      .map(
        (a) =>
          '<span class="builder-chip" data-action="' +
          escapeHTML(a) +
          '" style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 4px 2px 10px; background: var(--bg-panel-solid); border: 1px solid var(--border); border-radius: var(--r-md); font-size: 14px; font-family: monospace;">' +
          escapeHTML(a) +
          '<button type="button" class="builder-chip-remove" data-remove-action="' +
          escapeHTML(a) +
          '" aria-label="Remove ' +
          escapeHTML(a) +
          '" style="background: none; border: none; cursor: pointer; color: var(--text-tertiary); font-size: 16px; line-height: 1; padding: 0 4px;">×</button>' +
          "</span>",
      )
      .join(" ");
    if (builderState.actions.length === 0) {
      actionsHost.innerHTML =
        '<span id="builder-actions-empty" style="color: var(--text-tertiary); font-style: italic;">any tool</span>';
    } else {
      actionsHost.innerHTML = chips;
    }
  }
  const pathHost = document.getElementById("builder-path-host");
  if (pathHost) {
    const chips = builderState.paths
      .map(
        (p) =>
          '<span class="builder-chip" data-path="' +
          escapeHTML(p.path) +
          '" style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 4px 2px 10px; background: var(--bg-panel-solid); border: 1px solid var(--border); border-radius: var(--r-md); font-size: 14px; font-family: monospace;">' +
          escapeHTML(p.path) +
          '<button type="button" class="builder-chip-remove" data-remove-path="' +
          escapeHTML(p.path) +
          '" aria-label="Remove ' +
          escapeHTML(p.path) +
          '" style="background: none; border: none; cursor: pointer; color: var(--text-tertiary); font-size: 16px; line-height: 1; padding: 0 4px;">×</button>' +
          "</span>",
      )
      .join(" ");
    if (builderState.paths.length === 0) {
      pathHost.innerHTML =
        '<span id="builder-path-empty" style="color: var(--text-tertiary); font-style: italic;">any file</span>';
    } else {
      pathHost.innerHTML = chips;
    }
  }
  const branchHost = document.getElementById("builder-branches-host");
  if (branchHost) {
    if (builderState.branches.length === 0) {
      branchHost.innerHTML =
        '<span id="builder-branches-empty" style="color: var(--text-tertiary); font-style: italic; font-size: 12.5px;">any branch</span>';
    } else {
      branchHost.innerHTML = builderState.branches
        .map(
          (b) =>
            '<span class="builder-chip" data-branch="' +
            escapeHTML(b) +
            '" style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 4px 2px 10px; background: var(--bg-panel-solid); border: 1px solid var(--border); border-radius: var(--r-md); font-size: 12.5px; font-family: monospace;">' +
            escapeHTML(b) +
            '<button type="button" class="builder-chip-remove" data-remove-branch="' +
            escapeHTML(b) +
            '" aria-label="Remove ' +
            escapeHTML(b) +
            '" style="background: none; border: none; cursor: pointer; color: var(--text-tertiary); font-size: 14px; line-height: 1; padding: 0 4px;">×</button>' +
            "</span>",
        )
        .join(" ");
    }
  }
  const reasonEl = document.getElementById("builder-reason");
  if (reasonEl && reasonEl.value !== builderState.reason)
    reasonEl.value = builderState.reason;
  const graceRow = document.getElementById("builder-grace-row");
  if (graceRow) {
    graceRow.style.display =
      builderState.decision === "require_confirm" ? "" : "none";
  }
  const nameEl = document.getElementById("builder-name");
  if (nameEl && !nameEl.readOnly && !nameEl.value.trim()) {
    nameEl.placeholder = autoNameFromState();
  }
  renderPreviewSentence();
}

function truncMid(s, max) {
  if (!s || s.length <= max) return s || "";
  const head = Math.floor((max - 1) / 2);
  const tail = max - 1 - head;
  return s.slice(0, head) + "…" + s.slice(-tail);
}

// ---------- popovers ----------
// Single shared popover element from index.html. We render
// different content into it depending on which trigger was
// clicked. Outside click + Escape dismiss.
//
// Exported because src/priority-builder.js (Tier 1b) shares the
// same #builder-popover DOM host element. When/if a third
// builder wants this, fold it into a shared util module.
export function showBuilderPopover(triggerEl, html) {
  const host = document.getElementById("builder-popover");
  if (!host || !triggerEl) return;
  host.innerHTML =
    '<div class="card" style="padding: 8px; min-width: 220px; max-width: 320px;">' +
    html +
    "</div>";
  const rect = triggerEl.getBoundingClientRect();
  host.style.display = "block";
  const hostRect = host.getBoundingClientRect();
  let left = rect.left;
  if (left + hostRect.width > window.innerWidth - 8)
    left = window.innerWidth - hostRect.width - 8;
  if (left < 8) left = 8;
  host.style.left = left + "px";
  host.style.top = rect.bottom + 6 + "px";
  // Outside-click dismissal — registered on next tick so the
  // opening click doesn't immediately close.
  setTimeout(() => {
    const onDocClick = (e) => {
      if (host.contains(e.target)) return;
      if (triggerEl.contains(e.target)) return;
      hideBuilderPopover();
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") {
        hideBuilderPopover();
        document.removeEventListener("click", onDocClick);
        document.removeEventListener("keydown", onEsc);
      }
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onEsc);
  }, 0);
}

// Exported because src/priority-builder.js shares the same
// #builder-popover DOM host. Same reasoning as showBuilderPopover
// below.
export function hideBuilderPopover() {
  const host = document.getElementById("builder-popover");
  if (host) {
    host.style.display = "none";
    host.innerHTML = "";
  }
}

function openDecisionPopover(triggerEl) {
  const html = DECISION_OPTIONS.map(
    (o) =>
      '<button type="button" class="builder-pop-row" data-pick-decision="' +
      o.value +
      '" style="display: block; width: 100%; text-align: left; padding: 10px 12px; background: ' +
      (o.value === builderState.decision ? "var(--accent-bg)" : "transparent") +
      '; color: var(--text); border: none; border-radius: var(--r-sm); cursor: pointer; font-family: inherit;">' +
      '<div style="font-weight: 600; font-size: 13px;">' +
      escapeHTML(o.label) +
      "</div>" +
      '<div style="font-size: 12px; color: var(--text-secondary); margin-top: 2px;">' +
      escapeHTML(o.sub) +
      "</div>" +
      "</button>",
  ).join("");
  showBuilderPopover(triggerEl, html);
}

function openWhoPopover(triggerEl) {
  const cur = builderState.who;
  let html = "";
  html +=
    '<button type="button" class="builder-pop-row" data-pick-who="any" style="display: block; width: 100%; text-align: left; padding: 10px 12px; background: ' +
    (cur.kind === "any" ? "var(--accent-bg)" : "transparent") +
    '; color: var(--text); border: none; border-radius: var(--r-sm); cursor: pointer; font-family: inherit;">' +
    '<div style="font-weight: 600; font-size: 13px;">Any agent</div>' +
    '<div style="font-size: 12px; color: var(--text-secondary);">Rule applies to every agent.</div>' +
    "</button>";
  html +=
    '<div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-tertiary); font-weight: 600; padding: 8px 12px 4px;">By role</div>';
  for (const r of WHO_ROLE_OPTIONS) {
    const active = cur.kind === "role" && cur.role === r;
    html +=
      '<button type="button" class="builder-pop-row" data-pick-who="role" data-role="' +
      escapeHTML(r) +
      '" style="display: block; width: 100%; text-align: left; padding: 8px 12px; background: ' +
      (active ? "var(--accent-bg)" : "transparent") +
      '; color: var(--text); border: none; border-radius: var(--r-sm); cursor: pointer; font-family: inherit; font-size: 13px;">' +
      escapeHTML(r) +
      "</button>";
  }
  html +=
    '<div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-tertiary); font-weight: 600; padding: 8px 12px 4px;">Specific session</div>';
  html +=
    '<div style="padding: 4px 8px 8px;"><input type="text" id="builder-pop-agent-id" placeholder="claude-code/sess-…" value="' +
    (cur.kind === "agent_id" ? escapeHTML(cur.id) : "") +
    '" style="width: 100%; padding: 6px 8px; border: 1px solid var(--border); border-radius: var(--r-sm); background: var(--bg-panel-solid); color: var(--text); font-size: 12.5px; font-family: monospace; box-sizing: border-box;" /></div>';
  showBuilderPopover(triggerEl, html);
}

function openActionsPopover(triggerEl) {
  const selected = new Set(builderState.actions);
  let html =
    '<div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-tertiary); font-weight: 600; padding: 4px 12px;">Common tools</div>';
  for (const a of DEFAULT_ACTIONS) {
    const isOn = selected.has(a);
    html +=
      '<button type="button" class="builder-pop-row" data-toggle-action="' +
      escapeHTML(a) +
      '" style="display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 12px; background: transparent; color: var(--text); border: none; border-radius: var(--r-sm); cursor: pointer; font-family: inherit; font-size: 13px;">' +
      '<span style="display: inline-block; width: 14px; height: 14px; border: 1px solid var(--border-strong); border-radius: 3px; background: ' +
      (isOn ? "var(--accent)" : "transparent") +
      '; color: white; font-size: 11px; line-height: 14px; text-align: center;">' +
      (isOn ? "✓" : "") +
      "</span>" +
      '<span style="font-family: monospace;">' +
      escapeHTML(a) +
      "</span>" +
      "</button>";
  }
  html +=
    '<div style="border-top: 1px solid var(--border); margin-top: 6px; padding: 8px 12px;">';
  html +=
    '<label style="display: block; font-size: 11px; color: var(--text-tertiary); margin-bottom: 4px;">Custom tool</label>';
  html +=
    '<input type="text" id="builder-pop-custom-action" placeholder="e.g. Search, NotebookEdit" style="width: 100%; padding: 6px 8px; border: 1px solid var(--border); border-radius: var(--r-sm); background: var(--bg-panel-solid); color: var(--text); font-size: 12.5px; font-family: monospace; box-sizing: border-box;" />';
  html +=
    '<button type="button" id="builder-pop-custom-action-add" class="btn btn-sm" style="margin-top: 6px; font-size: 12px;">Add</button>';
  html += "</div>";
  showBuilderPopover(triggerEl, html);
}

function openPathPopover(triggerEl) {
  let html =
    '<div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-tertiary); font-weight: 600; padding: 4px 12px;">Common patterns</div>';
  const presets = [
    { glob: "**", label: "All files" },
    { glob: "**/secrets/**", label: "Secret stores" },
    { glob: "**/.env*", label: "Env files" },
    { glob: "**/package*.json", label: "Lockfiles" },
    { glob: "/payments/**", label: "Payments code" },
    { glob: "infra/prod/**", label: "Prod infra" },
  ];
  for (const p of presets) {
    html +=
      '<button type="button" class="builder-pop-row" data-pick-path="' +
      escapeHTML(p.glob) +
      '" style="display: flex; flex-direction: column; align-items: flex-start; width: 100%; padding: 8px 12px; background: transparent; color: var(--text); border: none; border-radius: var(--r-sm); cursor: pointer; font-family: inherit; text-align: left;">' +
      '<div style="font-size: 13px; font-weight: 500;">' +
      escapeHTML(p.label) +
      "</div>" +
      '<div style="font-size: 12px; color: var(--text-tertiary); font-family: monospace;">' +
      escapeHTML(p.glob) +
      "</div>" +
      "</button>";
  }
  html +=
    '<div style="border-top: 1px solid var(--border); margin-top: 6px; padding: 8px 12px;">';
  html +=
    '<label style="display: block; font-size: 11px; color: var(--text-tertiary); margin-bottom: 4px;">Custom path glob</label>';
  html +=
    '<input type="text" id="builder-pop-custom-path" placeholder="e.g. src/auth/**" style="width: 100%; padding: 6px 8px; border: 1px solid var(--border); border-radius: var(--r-sm); background: var(--bg-panel-solid); color: var(--text); font-size: 12.5px; font-family: monospace; box-sizing: border-box;" />';
  html +=
    '<button type="button" id="builder-pop-custom-path-add" class="btn btn-sm" style="margin-top: 6px; font-size: 12px;">Add</button>';
  html += "</div>";
  showBuilderPopover(triggerEl, html);
}

function openBranchPopover(triggerEl) {
  const selected = new Set(builderState.branches);
  const presets = ["main", "master", "develop", "staging", "production"];
  let html =
    '<div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-tertiary); font-weight: 600; padding: 4px 12px;">Common branches</div>';
  for (const b of presets) {
    const on = selected.has(b);
    html +=
      '<button type="button" class="builder-pop-row" data-toggle-branch="' +
      escapeHTML(b) +
      '" style="display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 12px; background: transparent; color: var(--text); border: none; border-radius: var(--r-sm); cursor: pointer; font-family: inherit; font-size: 13px;">' +
      '<span style="display: inline-block; width: 14px; height: 14px; border: 1px solid var(--border-strong); border-radius: 3px; background: ' +
      (on ? "var(--accent)" : "transparent") +
      '; color: white; font-size: 11px; line-height: 14px; text-align: center;">' +
      (on ? "✓" : "") +
      "</span>" +
      '<span style="font-family: monospace;">' +
      escapeHTML(b) +
      "</span>" +
      "</button>";
  }
  html +=
    '<div style="border-top: 1px solid var(--border); margin-top: 6px; padding: 8px 12px;">';
  html +=
    '<label style="display: block; font-size: 11px; color: var(--text-tertiary); margin-bottom: 4px;">Custom branch</label>';
  html +=
    '<input type="text" id="builder-pop-custom-branch" placeholder="e.g. release/2026-Q3" style="width: 100%; padding: 6px 8px; border: 1px solid var(--border); border-radius: var(--r-sm); background: var(--bg-panel-solid); color: var(--text); font-size: 12.5px; font-family: monospace; box-sizing: border-box;" />';
  html +=
    '<button type="button" id="builder-pop-custom-branch-add" class="btn btn-sm" style="margin-top: 6px; font-size: 12px;">Add</button>';
  html += "</div>";
  showBuilderPopover(triggerEl, html);
}

// ---------- live preview ----------

let _previewTimer = null;
let _previewSeq = 0;
function schedulePreviewUpdate() {
  if (_previewTimer) clearTimeout(_previewTimer);
  _previewTimer = setTimeout(runLivePreview, 400);
}

function renderPreviewSentence() {
  const el = document.getElementById("builder-preview-sentence");
  if (!el) return;
  let who;
  if (builderState.who.kind === "role") who = builderState.who.role + " agents";
  else if (builderState.who.kind === "agent_id")
    who = "session " + truncMid(builderState.who.id, 24);
  else who = "Any agent";
  const verb =
    {
      forbid: "will be blocked from running",
      audit: "would be blocked from running (audit only)",
      require_confirm: "must confirm before running",
    }[builderState.decision] || "will be blocked from running";
  const actions = builderState.actions.length
    ? humanList(builderState.actions)
    : "any tool";
  const paths = builderState.paths.length
    ? "matching " + humanList(builderState.paths.map((p) => p.path))
    : "any file";
  let line =
    "<strong>" +
    escapeHTML(who) +
    "</strong> " +
    escapeHTML(verb) +
    " <strong>" +
    escapeHTML(actions) +
    "</strong> on <strong>" +
    escapeHTML(paths) +
    "</strong>.";
  const quals = [];
  if (builderState.branches.length) {
    quals.push("branch " + escapeHTML(humanList(builderState.branches)));
  }
  const sen = Array.from(document.querySelectorAll("[data-seniority-check]"))
    .filter((cb) => cb.checked)
    .map((cb) => cb.dataset.seniorityCheck);
  if (sen.length) quals.push(escapeHTML(sen.join("/") + " seniority"));
  if (quals.length) {
    line +=
      ' <span style="color: var(--text-secondary); font-size: 13px;">(' +
      quals.join(" · ") +
      ")</span>";
  }
  el.innerHTML = line;
}

async function runLivePreview() {
  const statEl = document.getElementById("builder-preview-stat");
  if (!statEl) return;
  // Bump first: any answer still in flight is now stale, even when this
  // state isn't previewable.
  const seq = ++_previewSeq;
  const limit = builderLimitError();
  if (limit) {
    statEl.textContent = limit;
    return;
  }
  const rule = buildRuleFromState();
  if (!validateRuleForPreview(rule)) {
    statEl.textContent = "";
    return;
  }
  builderState.rule = rule;
  statEl.textContent = "Checking last 24h…";
  try {
    const resp = await apiFetch("/v1/rules/draft/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rule: rule }),
    });
    if (seq !== _previewSeq) return; // stale
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      statEl.innerHTML =
        '<span style="color: var(--danger);">' +
        escapeHTML(
          data.message || data.error || "Preview failed: " + resp.status,
        ) +
        "</span>";
      return;
    }
    builderState.previewResp = data;
    const wb = data.would_block || 0;
    const wc = data.would_require_confirm || 0;
    const ee = data.events_examined || 0;
    if (data.cloud_mode) {
      statEl.innerHTML =
        '<span style="color: var(--text-secondary);">Simulator runs daemon-side; cloud-mode preview shows the rule, but not the 24h sim.</span>';
    } else if (wb > 0 || wc > 0) {
      statEl.innerHTML =
        "<strong>" +
        (wb + wc) +
        "</strong> call(s) in the last 24h would have triggered this rule.";
    } else if (ee > 0) {
      statEl.innerHTML =
        '<span style="color: var(--text-secondary);">No matches in the last 24h (examined ' +
        ee +
        " events).</span>";
    } else {
      statEl.innerHTML =
        '<span style="color: var(--text-tertiary);">No recent activity to simulate against yet.</span>';
    }
  } catch (e) {
    if (seq !== _previewSeq) return;
    statEl.innerHTML =
      '<span style="color: var(--danger);">' +
      escapeHTML("Preview failed: " + (e.message || e)) +
      "</span>";
  }
}

function validateRuleForPreview(rule) {
  if (!rule) return false;
  if (rule.reason && rule.reason.length > 0) return true;
  const c = rule.forbid || rule.require_confirm;
  if (!c) return false;
  if (c.action && c.action.length) return true;
  if (c.resource && (c.resource.path || c.resource.branch)) return true;
  if (c.principal && (c.principal.role || c.principal.agent_id)) return true;
  return false;
}

// buildRuleFromState assembles a Rule JSON from builderState.
// Replaces the legacy collectFormToRule which read from bare
// inputs. Auto-generates a name from the sentence if the user
// hasn't supplied one in Advanced.
function buildRuleFromState() {
  const decision =
    builderState.decision === "require_confirm" ? "require_confirm" : "forbid";
  const mode = builderState.decision === "audit" ? "audit" : "enforce";
  const constraint = {};
  const principal = {};
  if (builderState.who.kind === "role")
    principal.role = [builderState.who.role];
  if (builderState.who.kind === "agent_id" && builderState.who.id)
    principal.agent_id = [builderState.who.id];
  const senChecks = Array.from(
    document.querySelectorAll("[data-seniority-check]"),
  )
    .filter((cb) => cb.checked)
    .map((cb) => cb.dataset.seniorityCheck);
  if (senChecks.length) principal.seniority = senChecks;
  const dom = (document.getElementById("builder-principal-domain") || {}).value || "";
  if (dom.trim())
    principal.domain = dom.split(",").map((s) => s.trim()).filter(Boolean);
  if (Object.keys(principal).length) constraint.principal = principal;
  if (builderState.actions.length)
    constraint.action = builderState.actions.slice();
  if (builderState.paths.length || builderState.branches.length) {
    constraint.resource = {};
    if (builderState.paths.length)
      constraint.resource.path = builderState.paths[0].path;
    if (builderState.branches.length) {
      constraint.resource.branch = builderState.branches[0];
      const branchInp = document.getElementById("builder-resource-branch");
      if (branchInp) branchInp.value = builderState.branches[0];
    }
  }
  const nameEl = document.getElementById("builder-name");
  let name = ((nameEl && nameEl.value) || "").trim();
  if (!name) name = autoNameFromState();
  const reason = (document.getElementById("builder-reason").value || "").trim();
  const rule = { name: name, reason: reason };
  if (decision === "forbid") rule.forbid = constraint;
  else {
    rule.require_confirm = constraint;
    const grace = parseInt(
      (document.getElementById("builder-grace") || {}).value || "0",
      10,
    );
    if (grace > 0) rule.grace_seconds = grace;
  }
  if (mode === "audit") rule.mode = "audit";
  return rule;
}

function autoNameFromState() {
  const dec =
    { forbid: "block", require_confirm: "ask", audit: "audit" }[
      builderState.decision
    ] || "rule";
  const who =
    builderState.who.kind === "role"
      ? builderState.who.role.toLowerCase()
      : builderState.who.kind === "agent_id"
        ? "session"
        : "any";
  const actions =
    builderState.actions.slice(0, 2).join("-").toLowerCase() || "any";
  let path = "";
  if (builderState.paths.length) {
    path = builderState.paths[0].path
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase();
    if (path.length > 24) path = path.slice(0, 24);
  }
  let name = [dec, who, actions, path].filter(Boolean).join("-");
  name = name.replace(/-+/g, "-").replace(/^-|-$/g, "");
  if (!name) name = "rule-" + Date.now();
  return name.slice(0, 64);
}

// mergePreservedFields takes the user's freshly-collected Rule
// and the original Rule from before they opened edit mode, and
// returns a Rule that has the user's edits PLUS the original's
// fields the form doesn't expose. Specifically:
//
//   - rule.unless (full clause) — preserved
//   - rule.disabled — preserved (editing must not re-enable a rule)
//   - rule.<decision>.resource.tags / command_content — preserved
//   - rule.<decision>.verb_class — preserved
//
// If the user changed the decision (forbid → require_confirm
// or vice versa), the preserved fields move into the new
// decision block. The Rule never has both forbid and
// require_confirm — the schema forbids it.
function mergePreservedFields(userRule, original) {
  if (!original) return userRule;
  if (original.unless) {
    userRule.unless = original.unless;
  }
  if (original.disabled) {
    userRule.disabled = true;
  }
  const origConstraint =
    original.Forbid ||
    original.forbid ||
    original.RequireConfirm ||
    original.require_confirm ||
    original.constraint;
  if (origConstraint) {
    const target = userRule.forbid || userRule.require_confirm;
    if (target) {
      const origVerbClass = origConstraint.verb_class;
      if (origVerbClass && origVerbClass.length && !target.verb_class) {
        target.verb_class = origVerbClass;
      }
      const origResource = origConstraint.resource;
      if (origResource) {
        const origTags = origResource.tags;
        if (origTags && origTags.length) {
          if (!target.resource) target.resource = {};
          if (!target.resource.tags) target.resource.tags = origTags;
        }
        if (origResource.command_content) {
          if (!target.resource) target.resource = {};
          if (!target.resource.command_content)
            target.resource.command_content = origResource.command_content;
        }
      }
    }
  }
  return userRule;
}

// builderLimitError is why the builder's state can't be saved as one
// rule, or "". A rule matches one path glob and one branch; extra chips
// used to be dropped silently on save.
function builderLimitError() {
  if (builderState.paths.length > 1)
    return "A rule matches one path. Use a glob (e.g. src/**) or make one rule per path.";
  if (builderState.branches.length > 1)
    return "A rule matches one branch. Make one rule per branch.";
  return "";
}

export async function handleBuilderSave(btn) {
  const limit = builderLimitError();
  if (limit) {
    document.getElementById("policy-builder-error").textContent = limit;
    return;
  }
  let rule = buildRuleFromState();
  if (builderState.editingOriginalRule) {
    rule = mergePreservedFields(rule, builderState.editingOriginalRule);
  }
  builderState.rule = rule;
  if (!rule.reason || !rule.reason.trim()) {
    document.getElementById("policy-builder-error").textContent =
      "Tell us why — the reason field is required.";
    return;
  }
  btn.disabled = true;
  btn.textContent = "Saving…";
  document.getElementById("policy-builder-error").textContent = "";
  try {
    const isEdit = !!builderState.editingName;
    const url = isEdit
      ? "/v1/rules/draft/" + encodeURIComponent(builderState.editingName)
      : "/v1/rules/draft/append";
    const method = isEdit ? "PUT" : "POST";
    const resp = await apiFetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rule: builderState.rule }),
    });
    const data = await resp.json();
    if (!resp.ok) {
      const msg = data.message || data.error || "HTTP " + resp.status;
      document.getElementById("policy-builder-error").textContent =
        "✗ Save failed: " + msg;
      btn.disabled = false;
      btn.textContent = "Save rule";
      return;
    }
    btn.disabled = false;
    btn.textContent = "Save rule";
    closePolicyBuilder();
    policyState.data = null;
    await renderPolicyPage();
    const verb = isEdit ? "updated" : "saved";
    showToast(
      "success",
      'Rule "' +
        (builderState.rule.name || "") +
        '" ' +
        verb +
        " + applied (now " +
        (data.rule_count || "?") +
        " rule(s) in machine policy).",
    );
  } catch (e) {
    document.getElementById("policy-builder-error").textContent =
      "✗ Save request failed: " + (e.message || e);
    btn.disabled = false;
    btn.textContent = "Save rule";
  }
}

export async function handleRuleToggleDisabled(name, currentlyDisabled, btn) {
  const verb = currentlyDisabled ? "Enable" : "Disable";
  const explainer = currentlyDisabled
    ? "Re-activates this rule. Matching activity will be governed again."
    : "Suspends this rule. It stays in the policy but won't fire on matching activity until you re-enable it. Useful for staging or debugging without losing the rule definition.";
  const ok = await showConfirm(verb + ' rule "' + name + '"?', explainer, verb);
  if (!ok) return;

  btn.disabled = true;
  const orig = btn.textContent;
  btn.textContent = (currentlyDisabled ? "Enabling" : "Disabling") + "…";

  try {
    const data = policyState.data || {};
    const rules = data.rules || [];
    const rule = rules.find((r) => r.name === name);
    if (!rule) {
      btn.disabled = false;
      btn.textContent = orig;
      showToast("error", 'Rule "' + name + '" not found in current policy.');
      return;
    }
    const updated = ruleToWriteShape(rule);
    if (currentlyDisabled) delete updated.disabled;
    else updated.disabled = true;
    const resp = await apiFetch("/v1/rules/draft/" + encodeURIComponent(name), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rule: updated }),
    });
    const respData = await resp.json().catch(() => ({}));
    if (resp.status === 404) {
      // A copy saved to this machine's file would lose to the repo/team
      // rule of the same name, so the toggle would silently do nothing.
      btn.disabled = false;
      btn.textContent = orig;
      showToast(
        "error",
        'Rule "' + name + '" comes from a repo or team policy, not this machine\'s file. ' + verb + " it there.",
      );
      return;
    }
    if (!resp.ok) {
      btn.disabled = false;
      btn.textContent = orig;
      showToast(
        "error",
        verb +
          " failed: " +
          (respData.message || respData.error || "unknown error"),
      );
      return;
    }
    policyState.data = null;
    await renderPolicyPage();
    showToast(
      "success",
      'Rule "' +
        name +
        '" ' +
        (currentlyDisabled ? "enabled" : "disabled") +
        ".",
    );
  } catch (e) {
    btn.disabled = false;
    btn.textContent = orig;
    showToast("error", verb + " request failed: " + (e.message || e));
  }
}

export async function handleRuleDelete(name, btn) {
  const ok = await showConfirm(
    'Delete rule "' + name + '"?',
    "This removes the rule from your policy. Any matching activity will no longer be governed by it. Audit history is preserved.",
    "Delete rule",
  );
  if (!ok) return;
  btn.disabled = true;
  const orig = btn.textContent;
  btn.textContent = "Deleting…";
  try {
    const resp = await apiFetch("/v1/rules/draft/" + encodeURIComponent(name), {
      method: "DELETE",
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      btn.disabled = false;
      btn.textContent = orig;
      showToast(
        "error",
        "Delete failed: " + (data.message || data.error || "unknown error"),
      );
      return;
    }
    policyState.data = null;
    await renderPolicyPage();
    showToast("success", 'Rule "' + name + '" deleted.');
  } catch (e) {
    btn.disabled = false;
    btn.textContent = orig;
    showToast("error", "Delete request failed: " + (e.message || e));
  }
}

// handleRuleEdit looks up the named rule in the cached
// policyState (already in memory; no extra fetch) and opens
// the builder modal in edit mode pre-filled with its fields.
export function handleRuleEdit(name) {
  const data = policyState.data || {};
  const rules = data.rules || [];
  const rule = rules.find((r) => r.name === name);
  if (!rule) {
    showToast("error", 'Could not find rule "' + name + '" in current ruleset.');
    return;
  }
  openPolicyBuilderForEdit(rule);
}

// ---- module init: wire seniority change + builder click + input listeners ----

function wireListeners() {
  // Builder change events. Triggers live preview when seniority
  // checkboxes flip.
  document.addEventListener("change", (e) => {
    if (e.target && e.target.dataset && e.target.dataset.seniorityCheck) {
      schedulePreviewUpdate();
      return;
    }
  });

  // Keyboard nav: Space + Enter on a focused popover trigger should
  // open the popover, matching the click affordance. The triggers
  // have tabindex="0" so they're already focusable.
  document.addEventListener("keydown", (e) => {
    if (e.key !== " " && e.key !== "Enter") return;
    const active = document.activeElement;
    if (!active) return;
    if (
      active.classList &&
      active.classList.contains("builder-popover-trigger")
    ) {
      e.preventDefault();
      active.click();
      return;
    }
    // Enter in a popover's custom box adds it, like its Add button.
    if (
      e.key === "Enter" &&
      /^builder-pop-custom-(action|path|branch)$/.test(active.id)
    ) {
      e.preventDefault();
      const add = document.getElementById(active.id + "-add");
      if (add) add.click();
    }
  });

  // Click delegation for popover triggers + picks.
  document.addEventListener("click", (e) => {
    const trig = e.target.closest(".builder-popover-trigger");
    if (trig) {
      // The Priority builder (still in bootstrap.js) also uses
      // .builder-popover-trigger for its own data-popover values
      // ("pri-decision", "pri-priority"). Only swallow the event
      // when this handler actually fired a popover — otherwise
      // bootstrap's #pri-decision-trigger handler must still see
      // the click.
      const kind = trig.dataset.popover;
      if (kind === "decision") {
        openDecisionPopover(trig);
        return;
      }
      if (kind === "who") {
        openWhoPopover(trig);
        return;
      }
    }
    const addAction = e.target.closest("#builder-add-action");
    if (addAction) {
      openActionsPopover(addAction);
      return;
    }
    const addPath = e.target.closest("#builder-add-path");
    if (addPath) {
      openPathPopover(addPath);
      return;
    }
    const addBranch = e.target.closest("#builder-add-branch");
    if (addBranch) {
      openBranchPopover(addBranch);
      return;
    }

    // Branch toggle from popover.
    const togBranch = e.target.closest("[data-toggle-branch]");
    if (togBranch) {
      const b = togBranch.dataset.toggleBranch;
      const i = builderState.branches.indexOf(b);
      if (i >= 0) builderState.branches.splice(i, 1);
      else builderState.branches.push(b);
      renderBuilderState();
      schedulePreviewUpdate();
      openBranchPopover(document.getElementById("builder-add-branch"));
      return;
    }
    if (e.target && e.target.id === "builder-pop-custom-branch-add") {
      const inp = document.getElementById("builder-pop-custom-branch");
      const v = ((inp && inp.value) || "").trim();
      if (v && !builderState.branches.includes(v)) {
        builderState.branches.push(v);
        renderBuilderState();
        schedulePreviewUpdate();
      }
      hideBuilderPopover();
      return;
    }
    const remBranch = e.target.closest("[data-remove-branch]");
    if (remBranch) {
      builderState.branches = builderState.branches.filter(
        (b) => b !== remBranch.dataset.removeBranch,
      );
      renderBuilderState();
      schedulePreviewUpdate();
      return;
    }

    const pickDec = e.target.closest("[data-pick-decision]");
    if (pickDec) {
      builderState.decision = pickDec.dataset.pickDecision;
      hideBuilderPopover();
      renderBuilderState();
      schedulePreviewUpdate();
      return;
    }
    const pickWho = e.target.closest("[data-pick-who]");
    if (pickWho) {
      const kind = pickWho.dataset.pickWho;
      if (kind === "any") builderState.who = { kind: "any" };
      else if (kind === "role")
        builderState.who = { kind: "role", role: pickWho.dataset.role };
      hideBuilderPopover();
      renderBuilderState();
      schedulePreviewUpdate();
      return;
    }
    const togAction = e.target.closest("[data-toggle-action]");
    if (togAction) {
      const a = togAction.dataset.toggleAction;
      const i = builderState.actions.indexOf(a);
      if (i >= 0) builderState.actions.splice(i, 1);
      else builderState.actions.push(a);
      renderBuilderState();
      schedulePreviewUpdate();
      openActionsPopover(document.getElementById("builder-add-action"));
      return;
    }
    if (e.target && e.target.id === "builder-pop-custom-action-add") {
      const inp = document.getElementById("builder-pop-custom-action");
      const v = ((inp && inp.value) || "").trim();
      if (v && !builderState.actions.includes(v)) {
        builderState.actions.push(v);
        renderBuilderState();
        schedulePreviewUpdate();
      }
      hideBuilderPopover();
      return;
    }
    const pickPath = e.target.closest("[data-pick-path]");
    if (pickPath) {
      const g = pickPath.dataset.pickPath;
      if (g && !builderState.paths.some((p) => p.path === g)) {
        builderState.paths.push({ path: g });
        renderBuilderState();
        schedulePreviewUpdate();
      }
      hideBuilderPopover();
      return;
    }
    if (e.target && e.target.id === "builder-pop-custom-path-add") {
      const inp = document.getElementById("builder-pop-custom-path");
      const v = ((inp && inp.value) || "").trim();
      if (v && !builderState.paths.some((p) => p.path === v)) {
        builderState.paths.push({ path: v });
        renderBuilderState();
        schedulePreviewUpdate();
      }
      hideBuilderPopover();
      return;
    }
    const remAction = e.target.closest("[data-remove-action]");
    if (remAction) {
      builderState.actions = builderState.actions.filter(
        (a) => a !== remAction.dataset.removeAction,
      );
      renderBuilderState();
      schedulePreviewUpdate();
      return;
    }
    const remPath = e.target.closest("[data-remove-path]");
    if (remPath) {
      builderState.paths = builderState.paths.filter(
        (p) => p.path !== remPath.dataset.removePath,
      );
      renderBuilderState();
      schedulePreviewUpdate();
      return;
    }
  });

  // In-popover input listeners.
  document.addEventListener("input", (e) => {
    if (e.target && e.target.id === "builder-pop-agent-id") {
      const v = e.target.value.trim();
      if (v) builderState.who = { kind: "agent_id", id: v };
      else builderState.who = { kind: "any" };
      renderBuilderState();
      schedulePreviewUpdate();
    }
    if (e.target && e.target.id === "builder-reason") {
      builderState.reason = e.target.value;
      schedulePreviewUpdate();
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
